
import React, { useEffect, useState } from 'react';
import { isSupabaseAvailable } from '@/lib/supabase';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AdminConnectivityMonitorProps {
  onConnectionChange?: (isConnected: boolean) => void;
  onRetryConnection?: () => Promise<boolean>;
  className?: string;
}

const AdminConnectivityMonitor = ({
  onConnectionChange,
  onRetryConnection,
  className = ''
}: AdminConnectivityMonitorProps) => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const { toast } = useToast();
  
  // Check connectivity regularly
  useEffect(() => {
    // Initial check
    checkConnectivity();
    
    // Set up periodic checks (every 30 seconds)
    const interval = setInterval(checkConnectivity, 30000);
    
    // Check connectivity when tab becomes active
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnectivity();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Check Supabase connectivity
  const checkConnectivity = async () => {
    if (isChecking) return;
    
    setIsChecking(true);
    try {
      const available = await isSupabaseAvailable();
      
      // Only update state and trigger callback if state changed
      if (isConnected !== available) {
        setIsConnected(available);
        if (onConnectionChange) {
          onConnectionChange(available);
        }
        
        // Only show toast on connection loss, not on reconnection
        if (!available && isConnected === true) {
          toast({
            title: "Mất kết nối",
            description: "Kết nối đến máy chủ bị gián đoạn. Đang chuyển sang chế độ ngoại tuyến.",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Error checking connectivity:', error);
      setIsConnected(false);
    } finally {
      setIsChecking(false);
    }
  };
  
  // Handler for manual retry
  const handleRetry = async () => {
    setIsChecking(true);
    try {
      if (onRetryConnection) {
        const result = await onRetryConnection();
        setIsConnected(result);
        
        // Only show toast on failure
        if (!result) {
          toast({
            title: "Kết nối thất bại",
            description: "Không thể kết nối đến máy chủ. Vui lòng thử lại sau.",
            variant: "destructive"
          });
        }
      } else {
        const available = await isSupabaseAvailable();
        setIsConnected(available);
        
        // Only show toast on failure
        if (!available) {
          toast({
            title: "Kết nối thất bại",
            description: "Không thể kết nối đến máy chủ. Vui lòng thử lại sau.",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Error retrying connection:', error);
      toast({
        title: "Lỗi kết nối",
        description: "Đã xảy ra lỗi khi thử kết nối lại.",
        variant: "destructive"
      });
    } finally {
      setIsChecking(false);
    }
  };
  
  // If connectivity check hasn't completed yet
  if (isConnected === null) {
    return null;
  }

  // When disconnected, show alert
  if (!isConnected) {
    return (
      <Alert variant="destructive" className={`mb-4 ${className}`}>
        <WifiOff className="h-4 w-4 mr-2" />
        <AlertTitle>Không thể kết nối đến máy chủ</AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p>Ứng dụng đang chạy ở chế độ ngoại tuyến. Dữ liệu sẽ được lưu trữ cục bộ.</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center bg-white"
            onClick={handleRetry}
            disabled={isChecking}
          >
            {isChecking ? (
              <span className="flex items-center">
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Đang kết nối...
              </span>
            ) : (
              <span className="flex items-center">
                <RefreshCw className="mr-2 h-4 w-4" />
                Thử kết nối lại
              </span>
            )}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  
  // When connected but with recent reconnection, show positive alert
  if (isConnected) {
    return (
      <div className={`mb-1 flex items-center text-sm text-green-600 ${className}`}>
        <Wifi className="h-3 w-3 mr-1" />
        <span>Đã kết nối</span>
      </div>
    );
  }
  
  return null;
};

export default React.memo(AdminConnectivityMonitor);
